/**
 * <ShareBackModal> — `external-sharing` Phase 4 Session 4.4 (2026-05-09).
 *
 * Recipient-side share-back form. Mounts at `<BuilderPage>` root + listens
 * for the `arcanum:share-back` window event dispatched by
 * `<ShareForkBanner>`'s `[Share back]` button (Session 4.3).
 *
 * Distinct from `<SendDealLinkModal>`:
 *   - NO recipient input — the recipient is implicit (the parent share's
 *     owner). Server resolves it from `parentShareId`.
 *   - Form is just an optional message field + a "Sharing back to:
 *     {ownerName}" preamble (`usePublicDeal(parentShareId)` resolves the
 *     owner name).
 *   - Success branch is a brief acknowledgement + Done button — NO
 *     copy-link panel (the recipient already knows the link; they just
 *     need confirmation that the share-back went through).
 *
 * Submit calls `shareForkBack({conversationId, parentShareId, message?})`.
 * The server's body schema field is `conversationId` (the fork
 * conversation), NOT `forkConversationId` — the window event payload uses
 * `forkConversationId` as a clearer name; we map at the call site.
 *
 * Realtime echo: server-side `SharePublisher.publishShareForkedBack`
 * (Phase 2 Session 2.2) fires on the original owner's `user:<ownerId>:
 * shares` channel. Their `useShareRealtime()` hook invalidates
 * `unreadCount()` + `received()` automatically — no client propagation
 * needed from this modal.
 *
 * Error branches:
 *   - 410 PARENT_SHARE_REVOKED → "The original share was revoked..."
 *   - 422 FORK_CHAIN_TOO_DEEP → "This deal has been re-shared too many
 *     times (5-hop cap)..."
 *   - Generic 4xx/5xx → friendly fallback
 */
import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { ApiError } from '@/lib/api';
import { shareForkBack } from '@/lib/shares-api';
import { usePublicDeal } from '@/hooks/use-public-deal';

const MESSAGE_MAX_LEN = 500;

export interface ShareBackModalProps {
  /** The fork conversation id — used as the open-event guard so cross-route
   *  mounts (if any future page also dispatches `arcanum:share-back`) don't
   *  open the wrong modal. When null, the modal is permanently inert (no
   *  fork conversation in scope). */
  conversationId: string | null;
  /** The parent share id (`conversation.forkedFromShareId`). When null, the
   *  modal is inert — there's no fork-back without a parent share. */
  forkParentShareId: string | null;
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; code?: string; message: string };

export function ShareBackModal({
  conversationId,
  forkParentShareId,
}: ShareBackModalProps) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });

  const parentDealQuery = usePublicDeal(forkParentShareId);

  const reset = useCallback(() => {
    setMessage('');
    setPhase({ kind: 'idle' });
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    reset();
  }, [reset]);

  // Listen for the `arcanum:share-back` window event. Guard on the
  // `forkConversationId` payload matching this modal's `conversationId`
  // prop so the right modal opens.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e: Event): void => {
      const detail = (e as CustomEvent<{
        forkConversationId?: string;
        parentShareId?: string;
      }>).detail;
      if (!detail?.forkConversationId) return;
      if (conversationId === null) return;
      if (detail.forkConversationId !== conversationId) return;
      // Defensive: parentShareId on the event must match the modal's
      // `forkParentShareId` prop. They should always agree (both come
      // from the same conversation row), but guard against drift.
      if (detail.parentShareId && detail.parentShareId !== forkParentShareId) {
        return;
      }
      setOpen(true);
    };
    window.addEventListener('arcanum:share-back', handler);
    return () => window.removeEventListener('arcanum:share-back', handler);
  }, [conversationId, forkParentShareId]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, handleClose]);

  if (!open) return null;
  if (conversationId === null || forkParentShareId === null) return null;

  const ownerName = parentDealQuery.data?.ownerName ?? 'the original sender';
  const messageCount = message.length;

  const handleSubmit = async (): Promise<void> => {
    if (messageCount > MESSAGE_MAX_LEN) {
      setPhase({
        kind: 'error',
        message: `Message must be ${MESSAGE_MAX_LEN} characters or fewer.`,
      });
      return;
    }
    setPhase({ kind: 'submitting' });
    try {
      await shareForkBack({
        conversationId,
        parentShareId: forkParentShareId,
        message: message.trim().length > 0 ? message.trim() : undefined,
      });
      setPhase({ kind: 'success' });
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined;
      let friendly: string;
      if (err instanceof ApiError && err.code === 'PARENT_SHARE_REVOKED') {
        friendly =
          'The original share was revoked. Share back is no longer possible.';
      } else if (err instanceof ApiError && err.code === 'FORK_CHAIN_TOO_DEEP') {
        friendly =
          'This deal has been re-shared too many times (chain depth 5). Start a new conversation instead.';
      } else if (err instanceof ApiError && err.code === 'NOT_FORKED_FROM_PARENT') {
        friendly =
          'This conversation isn’t a fork of the original share — share back is not available.';
      } else if (err instanceof ApiError && err.status >= 500) {
        friendly =
          'Something went wrong on our end. Try again or contact support.';
      } else if (err instanceof Error) {
        friendly = err.message;
      } else {
        friendly = 'Failed to share back.';
      }
      setPhase({ kind: 'error', code, message: friendly });
    }
  };

  const submitting = phase.kind === 'submitting';
  const showForm =
    phase.kind === 'idle' || phase.kind === 'submitting' || phase.kind === 'error';

  return (
    <>
      <div
        data-testid="share-back-modal-overlay"
        className="fixed inset-0 bg-void/70 z-50"
        onClick={handleClose}
      />
      <div
        data-testid="share-back-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-back-modal-title"
        className={clsx(
          'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50',
          'w-full max-w-lg bg-panel-bg border border-panel-border rounded-[3px]',
          'shadow-[0_8px_24px_rgba(0,0,0,0.4)]',
        )}
      >
        <div className="px-5 py-3 border-b border-panel-border flex items-center justify-between">
          <span
            id="share-back-modal-title"
            className="text-xs font-mono font-semibold text-text-muted tracking-[1.4px] uppercase"
          >
            Share back
          </span>
          <button
            type="button"
            data-testid="share-back-modal-close"
            onClick={handleClose}
            aria-label="Close"
            className="text-text-dim hover:text-text-bright text-base leading-none cursor-pointer"
          >
            ✕
          </button>
        </div>

        {showForm && (
          <div className="p-5 flex flex-col gap-4">
            <div
              data-testid="share-back-modal-preamble"
              className="text-sm font-body text-text leading-relaxed"
            >
              Sharing back to:{' '}
              <span className="font-mono text-text-bright">{ownerName}</span>
            </div>

            <div>
              <label
                htmlFor="share-back-modal-message"
                className="block text-xs text-text-muted font-mono uppercase tracking-[0.6px] mb-1.5"
              >
                Message{' '}
                <span className="text-text-dim normal-case tracking-normal">
                  (optional)
                </span>
              </label>
              <Textarea
                id="share-back-modal-message"
                data-testid="share-back-modal-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Here's my pass at the deal…"
                maxLength={MESSAGE_MAX_LEN}
                rows={3}
                disabled={submitting}
                autoFocus
              />
              <div className="flex justify-end mt-1.5">
                <span
                  data-testid="share-back-modal-message-count"
                  className={clsx(
                    'text-xs font-mono tabular-nums',
                    messageCount >= MESSAGE_MAX_LEN
                      ? 'text-warn'
                      : 'text-text-dim',
                  )}
                >
                  {messageCount}/{MESSAGE_MAX_LEN}
                </span>
              </div>
            </div>

            {phase.kind === 'error' && (
              <div
                data-testid="share-back-modal-error"
                data-error-code={phase.code ?? ''}
                className="px-3 py-2 border border-danger/15 bg-danger-dim rounded-[2px] text-sm font-mono text-danger"
              >
                {phase.message}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-panel-border -mx-5 px-5 pb-0">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={submitting}
                data-testid="share-back-modal-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleSubmit()}
                disabled={submitting}
                data-testid="share-back-modal-submit"
              >
                {submitting ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        )}

        {phase.kind === 'success' && (
          <div
            data-testid="share-back-modal-success"
            className="p-5 flex flex-col gap-4"
          >
            <div>
              <span className="text-xs font-mono font-semibold text-accent tracking-[0.6px] uppercase">
                Sent
              </span>
              <p className="text-sm font-body text-text mt-1 leading-relaxed">
                Your version is on its way back to{' '}
                <span className="font-mono text-text-bright">{ownerName}</span>.
                They&apos;ll see it in their Received tab.
              </p>
            </div>
            <div className="flex justify-end pt-2 border-t border-panel-border -mx-5 px-5 pb-0">
              <Button
                variant="primary"
                onClick={handleClose}
                data-testid="share-back-modal-done"
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
